import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: t("nav.home"), href: "/", isLink: true },
    { label: t("nav.categories"), href: "#categories" },
    { label: t("nav.spreadsheet"), href: "#articles" },
    { label: t("nav.guides"), href: "#articles" },
    { label: t("nav.safety"), href: "#faq" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
              Kakobuy <span className="text-primary-600">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) =>
              item.isLink ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {item.label}
                </a>
              )
            )}
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 bg-background-50/98 backdrop-blur-md max-h-[70vh] overflow-y-auto">
            <div className="py-2">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 py-3 px-4 text-sm font-semibold text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-home-4-line text-base"></i>
                </div>
                {t("nav.home")}
              </Link>

              <a
                href="#categories"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 py-3 px-4 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-base"></i>
                </div>
                {t("nav.categories")}
              </a>

              <div className="border-t border-background-200/70 my-2 mx-4"></div>

              <p className="px-4 py-1 text-xs text-foreground-500 font-medium uppercase tracking-wider">
                {t("nav.categoriesList")}
              </p>
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-4 text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-sm text-primary-500`}></i>
                  </div>
                  <span>{cat.namePl}</span>
                </a>
              ))}

              <div className="border-t border-background-200/70 my-2 mx-4"></div>

              <a
                href="#articles"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 py-3 px-4 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-article-line text-base"></i>
                </div>
                {t("nav.guides")}
              </a>

              <a
                href="#faq"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 py-3 px-4 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-shield-check-line text-base"></i>
                </div>
                {t("nav.safety")}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}